import React, {useState} from 'react';
import {ModalForm, ProFormDigit, ProFormGroup, ProFormSelect, ProFormText,} from '@ant-design/pro-components';
import {Button, Card, Collapse, Divider, message, Space, Tag, Typography, Upload} from 'antd';
import {InboxOutlined, UploadOutlined} from '@ant-design/icons';
import {useAtom, useSetAtom} from 'jotai';
import {useContent, useEnrichContent, useTopics, useUploadScreenshots, usePosts, useStories, useReels} from '../api';
import {InstagramContent} from '../types';
import InstagramThumbnail from './InstagramThumbnail';
import {contentQueryAtom, enrichModalVisibleAtom, selectedContentIdAtom,} from '../atoms';

const {Text, Title} = Typography;
const {Dragger} = Upload;
const {Panel} = Collapse;

const EnrichModal: React.FC = () => {
    const [modalVisible, setModalVisible] = useAtom(enrichModalVisibleAtom);
    const [selectedContentId] = useAtom(selectedContentIdAtom);
    const setSelectedContentId = useSetAtom(selectedContentIdAtom);
    const [queryParams] = useAtom(contentQueryAtom);
    const [screenshots, setScreenshots] = useState<any[]>([]);
    const [uploading, setUploading] = useState(false);

    const {data: topicsData} = useTopics();
    const {data: contentData} = useContent({page: 1, page_size: 1000}); // Use unfiltered content data for modal
    const {data: postsData} = usePosts({page: 1, page_size: 1000}); // Also fetch posts directly
    const {data: storiesData} = useStories({page: 1, page_size: 1000}); // Also fetch stories directly
    const {data: reelsData} = useReels({page: 1, page_size: 1000}); // Also fetch reels directly
    const enrichMutation = useEnrichContent();
    const uploadScreenshotsMutation = useUploadScreenshots();

    const topics = topicsData?.data || [];
    const content = contentData?.data || [];
    const posts = postsData?.data || [];
    const stories = storiesData?.data || [];
    const reels = reelsData?.data || [];
    
    // Try to find the selected content in any of the content arrays
    let selectedContent = content.find(c => c.id === selectedContentId) as InstagramContent;
    if (!selectedContent) {
        selectedContent = posts.find(p => p.id === selectedContentId) as unknown as InstagramContent;
    }
    if (!selectedContent) {
        selectedContent = stories.find(s => s.id === selectedContentId) as unknown as InstagramContent;
    }
    if (!selectedContent) {
        selectedContent = reels.find(r => r.id === selectedContentId) as unknown as InstagramContent;
    }
    

    const handleDataSubmit = async (values: any) => {
        if (!selectedContentId) return false;

        try {
            console.log('Enrichment values:', values);

            // Create JSON payload for comprehensive enrichment (without screenshots)
            const payload = {
                contentId: selectedContentId,
                topicId: values.topic_id || '',
                data: {}
            };

            // Add all form values to data object (except topic_id which is handled separately)
            Object.keys(values).forEach(key => {
                if (values[key] !== undefined && values[key] !== null && key !== 'topic_id') {
                    payload.data[key] = values[key];
                }
            });

            await enrichMutation.mutateAsync({ 
                payload, 
                contentType: selectedContent.content_type.toLowerCase() 
            });
            message.success('Content enrichment data saved successfully!');
            return true;
        } catch (error) {
            console.error('Error submitting form:', error);
            message.error('Failed to save enrichment data');
            return false;
        }
    };

    const handleScreenshotUpload = async () => {
        if (!selectedContentId || screenshots.length === 0) {
            message.warning('Please select at least one screenshot to upload');
            return;
        }

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('contentId', selectedContentId);

            // Append multiple screenshots
            screenshots.forEach((file, index) => {
                formData.append('screenshots', file.originFileObj);
            });

            await uploadScreenshotsMutation.mutateAsync(formData);
            message.success('Screenshots uploaded and analyzed successfully!');
            setScreenshots([]);
        } catch (error) {
            console.error('Error uploading screenshots:', error);
            message.error('Failed to upload screenshots');
        } finally {
            setUploading(false);
        }
    };

    const handleClose = () => {
        setModalVisible(false);
        setSelectedContentId(null);
        setScreenshots([]);
    };

    const uploadProps = {
        name: 'screenshots',
        multiple: true,
        accept: 'image/*',
        beforeUpload: () => false, // Prevent auto upload
        fileList: screenshots,
        onChange: (info: any) => {
            setScreenshots(info.fileList);
        },
        onRemove: (file: any) => {
            setScreenshots(screenshots.filter(item => item.uid !== file.uid));
        },
    };

    const renderContentTypeSpecificFields = (contentType: string) => {
        switch (contentType.toLowerCase()) {
            case 'post':
                return renderPostFields();
            case 'story':
                return renderStoryFields();
            case 'reel':
                return renderReelFields();
            default:
                return renderBasicFields();
        }
    };

    const renderPostFields = () => (
        <Collapse defaultActiveKey={['quick-stats']} ghost>
            <Panel header="Post Information" key="post-info">
                <ProFormGroup>
                    <ProFormText
                        name="caption_snippet"
                        label="Caption Snippet"
                        placeholder="Truncated caption text"
                    />
                    <ProFormText
                        name="post_date"
                        label="Post Date"
                        placeholder="Date when posted"
                    />
                    <ProFormSelect
                        name="post_type"
                        label="Post Type"
                        options={[
                            {label: 'Image', value: 'Image'},
                            {label: 'Carousel', value: 'Carousel'},
                            {label: 'Video', value: 'Video'}
                        ]}
                    />
                </ProFormGroup>
            </Panel>

            <Panel header="Quick Stats" key="quick-stats">
                <ProFormGroup>
                    <ProFormDigit name="likes" label="Likes" min={0} fieldProps={{precision: 0}}/>
                    <ProFormDigit name="comments" label="Comments" min={0} fieldProps={{precision: 0}}/>
                    <ProFormDigit name="shares" label="Shares" min={0} fieldProps={{precision: 0}}/>
                    <ProFormDigit name="saves" label="Saves" min={0} fieldProps={{precision: 0}}/>
                </ProFormGroup>
            </Panel>

            <Panel header="Overview" key="overview">
                <ProFormGroup>
                    <ProFormDigit name="accounts_reached" label="Accounts Reached" min={0} fieldProps={{precision: 0}}/>
                    <ProFormDigit name="impressions" label="Impressions" min={0} fieldProps={{precision: 0}}/>
                    <ProFormDigit name="profile_activity" label="Profile Activity" min={0} fieldProps={{precision: 0}}/>
                </ProFormGroup>
            </Panel>

            <Panel header="Discovery & Impressions" key="discovery">
                <ProFormGroup>
                    <ProFormDigit name="total_impressions" label="Total Impressions" min={0}
                                  fieldProps={{precision: 0}}/>
                    <ProFormDigit name="percentage_not_following" label="% Not Following" min={0} max={100}
                                  fieldProps={{precision: 2}}/>
                    <ProFormDigit name="impressions_from_home" label="From Home" min={0} fieldProps={{precision: 0}}/>
                    <ProFormDigit name="impressions_from_hashtags" label="From Hashtags" min={0}
                                  fieldProps={{precision: 0}}/>
                    <ProFormDigit name="impressions_from_profile" label="From Profile" min={0}
                                  fieldProps={{precision: 0}}/>
                    <ProFormDigit name="impressions_from_explore" label="From Explore" min={0}
                                  fieldProps={{precision: 0}}/>
                    <ProFormDigit name="impressions_from_other" label="From Other" min={0} fieldProps={{precision: 0}}/>
                </ProFormGroup>
            </Panel>

            <Panel header="Profile Activity" key="profile-activity">
                <ProFormGroup>
                    <ProFormDigit name="profile_visits" label="Profile Visits" min={0} fieldProps={{precision: 0}}/>
                    <ProFormDigit name="follows" label="Follows" min={0} fieldProps={{precision: 0}}/>
                    <ProFormDigit name="website_taps" label="Website Taps" min={0} fieldProps={{precision: 0}}/>
                    <ProFormDigit name="email_button_taps" label="Email Button Taps" min={0} fieldProps={{precision: 0}}/>
                </ProFormGroup>
            </Panel>
        </Collapse>
    );

    const renderStoryFields = () => (
        <Collapse defaultActiveKey={['overview']} ghost>
            <Panel header="Overview" key="overview">
                <ProFormGroup>
                    <ProFormDigit name="views" label="Views" min={0} fieldProps={{precision: 0}}/>
                    <ProFormDigit name="interactions" label="Interactions" min={0} fieldProps={{precision: 0}}/>
                    <ProFormDigit name="profile_activity" label="Profile Activity" min={0} fieldProps={{precision: 0}}/>
                </ProFormGroup>
            </Panel>

            <Panel header="Views Details" key="views-details">
                <ProFormGroup>
                    <ProFormDigit name="total_views" label="Total Views" min={0} fieldProps={{precision: 0}}/>
                    <ProFormDigit name="followers_percentage" label="Followers %" min={0} max={100}
                                  fieldProps={{precision: 2}}/>
                    <ProFormDigit name="non_followers_percentage" label="Non-Followers %" min={0} max={100}
                                  fieldProps={{precision: 2}}/>
                    <ProFormDigit name="accounts_reached" label="Accounts Reached" min={0} fieldProps={{precision: 0}}/>
                </ProFormGroup>
            </Panel>

            <Panel header="Interactions" key="interactions">
                <ProFormGroup>
                    <ProFormDigit name="likes" label="Likes" min={0} fieldProps={{precision: 0}}/>
                    <ProFormDigit name="replies" label="Replies" min={0} fieldProps={{precision: 0}}/>
                    <ProFormDigit name="shares" label="Shares" min={0} fieldProps={{precision: 0}}/>
                    <ProFormDigit name="accounts_engaged" label="Accounts Engaged" min={0} fieldProps={{precision: 0}}/>
                    <ProFormDigit name="sticker_taps_total" label="Sticker Taps Total" min={0}
                                  fieldProps={{precision: 0}}/>
                </ProFormGroup>
            </Panel>

            <Panel header="Navigation" key="navigation">
                <ProFormGroup>
                    <ProFormDigit name="navigation_total" label="Navigation Total" min={0} fieldProps={{precision: 0}}/>
                    <ProFormDigit name="forwards" label="Forwards" min={0} fieldProps={{precision: 0}}/>
                    <ProFormDigit name="back" label="Back" min={0} fieldProps={{precision: 0}}/>
                    <ProFormDigit name="next_story" label="Next Story" min={0} fieldProps={{precision: 0}}/>
                    <ProFormDigit name="exited" label="Exited" min={0} fieldProps={{precision: 0}}/>
                </ProFormGroup>
            </Panel>

            <Panel header="Profile Activity" key="profile-activity">
                <ProFormGroup>
                    <ProFormDigit name="profile_visits" label="Profile Visits" min={0} fieldProps={{precision: 0}}/>
                    <ProFormDigit name="follows" label="Follows" min={0} fieldProps={{precision: 0}}/>
                    <ProFormDigit name="external_link_taps" label="External Link Taps" min={0}
                                  fieldProps={{precision: 0}}/>
                </ProFormGroup>
            </Panel>
        </Collapse>
    );

    const renderReelFields = () => (
        <Collapse defaultActiveKey={['quick-stats']} ghost>
            <Panel header="Reel Information" key="reel-info">
                <ProFormGroup>
                    <ProFormText name="caption_snippet" label="Caption Snippet" placeholder="Truncated caption text"/>
                    <ProFormText name="post_date" label="Post Date" placeholder="Date when posted"/>
                    <ProFormDigit name="duration_seconds" label="Duration (seconds)" min={0}
                                  fieldProps={{precision: 0}}/>
                </ProFormGroup>
            </Panel>

            <Panel header="Quick Stats" key="quick-stats">
                <ProFormGroup>
                    <ProFormDigit name="likes" label="Likes" min={0} fieldProps={{precision: 0}}/>
                    <ProFormDigit name="comments" label="Comments" min={0} fieldProps={{precision: 0}}/>
                    <ProFormDigit name="shares" label="Shares" min={0} fieldProps={{precision: 0}}/>
                    <ProFormDigit name="saves" label="Saves" min={0} fieldProps={{precision: 0}}/>
                </ProFormGroup>
            </Panel>

            <Panel header="Overview" key="overview">
                <ProFormGroup>
                    <ProFormDigit name="views" label="Views" min={0} fieldProps={{precision: 0}}/>
                    <ProFormText name="watch_time" label="Watch Time" placeholder="e.g., 2h 30m"/>
                    <ProFormDigit name="interactions" label="Interactions" min={0} fieldProps={{precision: 0}}/>
                    <ProFormDigit name="profile_activity" label="Profile Activity" min={0} fieldProps={{precision: 0}}/>
                </ProFormGroup>
            </Panel>

            <Panel header="Views Details" key="views-details">
                <ProFormGroup>
                    <ProFormDigit name="total_views" label="Total Views" min={0} fieldProps={{precision: 0}}/>
                    <ProFormDigit name="followers_percentage" label="Followers %" min={0} max={100}
                                  fieldProps={{precision: 2}}/>
                    <ProFormDigit name="non_followers_percentage" label="Non-Followers %" min={0} max={100}
                                  fieldProps={{precision: 2}}/>
                    <ProFormDigit name="accounts_reached" label="Accounts Reached" min={0} fieldProps={{precision: 0}}/>
                </ProFormGroup>
            </Panel>

            <Panel header="Top Sources" key="top-sources">
                <ProFormGroup>
                    <ProFormText name="source_feed" label="Feed %" placeholder="e.g., 45%"/>
                    <ProFormText name="source_reels_tab" label="Reels Tab %" placeholder="e.g., 30%"/>
                    <ProFormText name="source_stories" label="Stories %" placeholder="e.g., 10%"/>
                    <ProFormText name="source_explore" label="Explore %" placeholder="e.g., 10%"/>
                    <ProFormText name="source_profile" label="Profile %" placeholder="e.g., 5%"/>
                </ProFormGroup>
            </Panel>

            <Panel header="Watch Time & Retention" key="retention">
                <ProFormGroup>
                    <ProFormText name="total_watch_time" label="Total Watch Time" placeholder="e.g., 2h 30m"/>
                    <ProFormDigit name="average_watch_time_seconds" label="Avg Watch Time (s)" min={0}
                                  fieldProps={{precision: 0}}/>
                    <ProFormText name="this_reel_view_rate" label="This Reel View Rate" placeholder="e.g., 75%"/>
                    <ProFormText name="your_typical_view_rate" label="Your Typical View Rate" placeholder="e.g., 68%"/>
                </ProFormGroup>
            </Panel>

            <Panel header="Profile Activity" key="profile-activity">
                <ProFormGroup>
                    <ProFormDigit name="accounts_engaged" label="Accounts Engaged" min={0} fieldProps={{precision: 0}}/>
                    <ProFormDigit name="profile_total" label="Profile Total" min={0} fieldProps={{precision: 0}}/>
                    <ProFormDigit name="follows" label="Follows" min={0} fieldProps={{precision: 0}}/>
                </ProFormGroup>
            </Panel>
        </Collapse>
    );

    const renderBasicFields = () => (
        <ProFormGroup title="Basic Metrics">
            <ProFormDigit name="likes" label="Likes" min={0} fieldProps={{precision: 0}}/>
            <ProFormDigit name="comments" label="Comments" min={0} fieldProps={{precision: 0}}/>
            <ProFormDigit name="shares" label="Shares" min={0} fieldProps={{precision: 0}}/>
            <ProFormDigit name="views" label="Views" min={0} fieldProps={{precision: 0}}/>
        </ProFormGroup>
    );

    if (!selectedContent) {
        return null;
    }

    return (
        <ModalForm
            title="Enrich Content"
            open={modalVisible}
            onOpenChange={(open) => {
                if (!open) handleClose();
            }}
            onFinish={handleDataSubmit}
            modalProps={{
                destroyOnClose: true,
                width: 900,
            }}
            submitter={{
                submitButtonProps: {
                    loading: enrichMutation.isLoading,
                    children: 'Save Data',
                },
            }}
            layout="horizontal"
            labelCol={{span: 6}}
            wrapperCol={{span: 18}}
            initialValues={
                selectedContent.is_enriched
                    ? {
                        topic_id: selectedContent.topic_id,
                        // Use properties directly from selectedContent
                        likes: selectedContent.likes,
                        comments: selectedContent.comments,
                        shares: selectedContent.shares,
                        views: selectedContent.views,
                        // Add other properties as needed based on content type
                    }
                    : {}
            }
        >
            <Card style={{marginBottom: 16}} size="small">
                <Space direction="vertical" style={{width: '100%'}}>
                    <Title level={5}>Content Information</Title>
                    <Space>
                        <InstagramThumbnail
                            url={selectedContent.media_url}
                            thumbnailS3Key={selectedContent.thumbnail_s3_key}
                            thumbnailPublicURL={selectedContent.thumbnail_public_url}
                            mediaType={selectedContent.content_type}
                            alt={`${selectedContent.content_type} by @${selectedContent.creator_username}`}
                            size={60}
                        />
                        <div>
                            <Text strong>@{selectedContent.creator_username}</Text>
                            <br/>
                            <Tag color="blue">{selectedContent.content_type.toUpperCase()}</Tag>
                            <br/>
                            <Text type="secondary" ellipsis style={{maxWidth: 300}}>
                                {selectedContent.caption}
                            </Text>
                        </div>
                    </Space>
                </Space>
            </Card>

            <ProFormSelect
                name="topic_id"
                label="Topic"
                placeholder="Select a topic"
                options={topics.map(topic => ({
                    label: (
                        <Space>
                            <div
                                style={{
                                    width: 12,
                                    height: 12,
                                    backgroundColor: topic.color,
                                    borderRadius: 2,
                                }}
                            />
                            {topic.name}
                        </Space>
                    ),
                    value: topic.id,
                }))}
                // rules={[{required: true, message: 'Please select a topic'}]}
            />

            <Divider>Screenshot Upload & AI Analysis</Divider>

            <div style={{marginBottom: 16}}>
                <label style={{display: 'block', marginBottom: 8, fontWeight: 'bold'}}>
                    Screenshots for AI Analysis (Multiple allowed)
                </label>
                <Dragger {...uploadProps}>
                    <p className="ant-upload-drag-icon">
                        <InboxOutlined/>
                    </p>
                    <p className="ant-upload-text">Click or drag multiple screenshots to upload</p>
                    <p className="ant-upload-hint">
                        Upload screenshots of the content for Gemini AI analysis. Multiple images will be analyzed
                        together.
                    </p>
                </Dragger>

                {screenshots.length > 0 && (
                    <div style={{marginTop: 16}}>
                        <Button
                            type="primary"
                            icon={<UploadOutlined/>}
                            onClick={handleScreenshotUpload}
                            loading={uploading || uploadScreenshotsMutation.isLoading}
                            disabled={screenshots.length === 0}
                        >
                            Upload & Analyze {screenshots.length} Screenshot{screenshots.length > 1 ? 's' : ''}
                        </Button>
                        <Text type="secondary" style={{marginLeft: 8}}>
                            This will send the screenshots to Gemini AI for analysis
                        </Text>
                    </div>
                )}
            </div>

            <Divider>Manual Data Entry (Content Type Specific)</Divider>

            {renderContentTypeSpecificFields(selectedContent.content_type)}
        </ModalForm>
    );
};

export default EnrichModal;
